import React, { startTransition, useEffect, useRef, useState } from "react";
import InternalDetailsPanel from "./consult_note_workspace/internal_details_panel";
import {
  type ConsultNoteDraftStatus,
  type ConsultNoteFlash,
  type ConsultNoteFormSections,
  type ConsultNotePendingAction,
  type ConsultNoteRevisionCompareResponse,
  type ConsultNoteRevisionHistoryResponse,
  type ConsultNoteRevisionSummary,
  type ConsultNoteSectionKey,
  type ConsultNoteTextSectionKey,
  type ConsultNoteVisit,
  type ConsultNoteWorkspace,
  type ConsultNoteWorkspaceResponse,
} from "./consult_note_workspace/types";
import {
  appendUniquePlanText,
  buildNoteCopyText,
  buildMutationFormData,
  createFormSections,
  isJsonObject,
  serializeMutationPayload,
} from "./consult_note_workspace/utils";
import { FlashBanner } from "./consult_note_workspace/primitives";
import {
  AddendumSectionCard,
  AssessmentSectionCard,
  PhysicalExamSectionCard,
  TextSectionCard,
  VisitDetailsCard,
} from "./consult_note_workspace/sections";
import {
  AiTracePanel,
  FormActions,
  MissingLabExtractionNotice,
  MultipleConsultIntakesNotice,
  RevisionHistoryPanel,
  TebraExportFailureNotice,
  WorkspaceHeader,
} from "./consult_note_workspace/panels";

const UNSAVED_CHANGES_MESSAGE = "You have unsaved consult note changes. Leave without saving?";
const REGENERATE_CONFIRM_MESSAGE = "Regenerate this draft from the latest template? Unsigned edits will be replaced.";
const SIGN_CONFIRM_MESSAGE = "Sign this consult note? A final PDF will be generated and queued for Tebra export.";
const SIGN_ADDENDUM_CONFIRM_MESSAGE = "Sign this addendum? The original signed date will stay unchanged and a new PDF will be queued for Tebra export.";
const RETRY_TEBRA_EXPORT_CONFIRM_MESSAGE =
  "Retry Tebra submission for this consult note? If the previous attempt partially succeeded, Tebra may receive another document.";
const DISCARD_ADDENDUM_DRAFT_CONFIRM_MESSAGE = "Create an updated draft and discard the addendum draft?";
const EDITOR_FORM_ID = "consult-note-editor-form";

type PendingAction = ConsultNotePendingAction | null;
type WorkspaceView = "editor" | "history" | "billing";

async function parseWorkspaceResponse(response: Response) {
  try {
    const payload: unknown = await response.json();
    if (!isJsonObject(payload) || !("bootstrap" in payload)) return null;

    return payload as ConsultNoteWorkspaceResponse;
  } catch (_error) {
    return null;
  }
}

async function parseRevisionHistoryResponse(response: Response) {
  try {
    const payload: unknown = await response.json();
    if (!isJsonObject(payload) || !Array.isArray(payload.revisions)) return null;

    return payload as ConsultNoteRevisionHistoryResponse;
  } catch (_error) {
    return null;
  }
}

async function parseRevisionCompareResponse(response: Response) {
  try {
    const payload: unknown = await response.json();
    if (!isJsonObject(payload) || !("revision" in payload) || !("snapshot" in payload) || !("diff" in payload)) return null;

    return payload as ConsultNoteRevisionCompareResponse;
  } catch (_error) {
    return null;
  }
}

async function copyTextToClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.className = "sr-only";
  document.body.appendChild(textarea);
  textarea.select();
  textarea.setSelectionRange(0, text.length);

  try {
    if (!document.execCommand("copy")) {
      throw new Error("Copy command failed");
    }
  } finally {
    textarea.remove();
  }
}

export default function ConsultNoteWorkspace({
  bootstrap,
  draftStatus: draftStatusProp,
  workspace: workspaceProp,
  flash: flashProp,
}: ConsultNoteWorkspaceResponse) {
  const [workspace, setWorkspace] = useState<ConsultNoteWorkspace | null>(workspaceProp || null);
  const [draftStatus, setDraftStatus] = useState<ConsultNoteDraftStatus | null>(draftStatusProp || null);
  const [flash, setFlash] = useState<ConsultNoteFlash | null>(flashProp || null);
  const [visit, setVisit] = useState<ConsultNoteVisit | null>(() => (workspaceProp ? { ...workspaceProp.visit } : null));
  const [noteTitle, setNoteTitle] = useState(() => workspaceProp?.note.title || "");
  const [draftAddendumBody, setDraftAddendumBody] = useState(() => workspaceProp?.draftAddendumBody || "");
  const [sections, setSections] = useState<ConsultNoteFormSections | null>(() =>
    workspaceProp ? createFormSections(workspaceProp.sections) : null
  );
  const [isInitializing, setIsInitializing] = useState(workspaceProp == null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const [retryBuild, setRetryBuild] = useState(false);
  const [activeView, setActiveView] = useState<WorkspaceView>("editor");
  const [detailsDirty, setDetailsDirty] = useState(false);
  const [revisionHistory, setRevisionHistory] = useState<ConsultNoteRevisionSummary[]>([]);
  const [selectedRevision, setSelectedRevision] = useState<ConsultNoteRevisionCompareResponse | null>(null);
  const [loadingRevisionId, setLoadingRevisionId] = useState<number | null>(null);
  const navigationConfirmedRef = useRef(false);
  const copyResetTimerRef = useRef<number | null>(null);

  useEffect(() => {
    startTransition(() => {
      setWorkspace(workspaceProp || null);
      setDraftStatus(draftStatusProp || null);
      setFlash(flashProp || null);
      setVisit(workspaceProp ? { ...workspaceProp.visit } : null);
      setNoteTitle(workspaceProp?.note.title || "");
      setDraftAddendumBody(workspaceProp?.draftAddendumBody || "");
      setSections(workspaceProp ? createFormSections(workspaceProp.sections) : null);
      setIsInitializing(workspaceProp == null && draftStatusProp?.state !== "failed");
      setPendingAction(null);
      setCopyState("idle");
      setActiveView("editor");
      setRevisionHistory([]);
      setSelectedRevision(null);
      setLoadingRevisionId(null);
    });
  }, [draftStatusProp, flashProp, workspaceProp]);

  useEffect(() => () => {
    if (copyResetTimerRef.current !== null) {
      window.clearTimeout(copyResetTimerRef.current);
    }
  }, []);

  useEffect(() => {
    const historyPath = workspace?.paths.history;
    if (activeView !== "history") {
      return undefined;
    }

    if (!workspace || !historyPath) {
      return undefined;
    }
    const safeHistoryPath = historyPath;

    let cancelled = false;

    async function loadRevisionHistory() {
      try {
        const response = await fetch(safeHistoryPath, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          credentials: "same-origin",
        });
        const payload = await parseRevisionHistoryResponse(response);
        if (cancelled || !response.ok || !payload) return;

        setRevisionHistory(payload.revisions);
        setSelectedRevision((current) =>
          current && payload.revisions.some((revision) => revision.id === current.revision.id) ? current : null
        );
      } catch (_error) {
        if (!cancelled) setRevisionHistory([]);
      }
    }

    loadRevisionHistory();

    return () => {
      cancelled = true;
    };
  }, [activeView, workspace, workspace?.note.latestRevisionNumber, workspace?.paths.history]);

  useEffect(() => {
    if (activeView !== "history" || revisionHistory.length === 0 || selectedRevision || loadingRevisionId !== null) return;

    void selectRevision(revisionHistory[0].id);
  }, [activeView, loadingRevisionId, revisionHistory, selectedRevision]);

  useEffect(() => {
    if (draftStatus && loadAttempt === 0) return undefined;
    if (workspace) return undefined;

    let cancelled = false;

    async function loadInitialWorkspace() {
      setIsInitializing(true);
      const separator = bootstrap.loadPath.includes("?") ? "&" : "?";
      const loadPath = retryBuild ? `${bootstrap.loadPath}${separator}retry_build=1` : bootstrap.loadPath;

      try {
        const response = await fetch(loadPath, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          credentials: "same-origin",
        });
        const payload = await parseWorkspaceResponse(response);
        if (cancelled) return;

        if (!response.ok) {
          applyWorkspaceResponse(payload, "Unable to generate the consult note draft.");
          return;
        }

        applyWorkspaceResponse(payload, "Unable to generate the consult note draft.");
      } catch (_error) {
        if (!cancelled) {
          setFlash({ alert: "Unable to generate the consult note draft." });
        }
      } finally {
        if (!cancelled) {
          setRetryBuild(false);
          setIsInitializing(false);
        }
      }
    }

    loadInitialWorkspace();

    return () => {
      cancelled = true;
    };
  }, [bootstrap.loadPath, loadAttempt, retryBuild, workspace]);

  useEffect(() => {
    if (workspace || !draftStatus || draftStatus.state === "failed") return undefined;

    const timerId = window.setTimeout(() => {
      setLoadAttempt((current) => current + 1);
    }, draftStatus.pollIntervalMs || 1000);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [draftStatus, workspace]);

  const initialSections = workspace ? createFormSections(workspace.sections) : null;
  const initialVisit = workspace ? { ...workspace.visit } : null;
  const hasUnsavedAddendumChanges = Boolean(workspace && draftAddendumBody !== (workspace.draftAddendumBody || ""));
  const hasUnsavedChanges = Boolean(
    detailsDirty ||
    workspace &&
      visit &&
      sections &&
      initialVisit &&
      initialSections &&
      (serializeMutationPayload(noteTitle, sections, visit) !== serializeMutationPayload(workspace.note.title, initialSections, initialVisit) ||
        hasUnsavedAddendumChanges)
  );
  const isMutating = pendingAction !== null;
  const isRegenerating = pendingAction === "regenerate";

  useEffect(() => {
    if (hasUnsavedChanges) {
      navigationConfirmedRef.current = false;
    }
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!workspace || !hasUnsavedChanges || (workspace.note.readOnly && !detailsDirty)) return undefined;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (navigationConfirmedRef.current) return;

      event.preventDefault();
      event.returnValue = "";
    };

    const handleBeforeVisit = (event: Event) => {
      if (navigationConfirmedRef.current) return;

      const nextUrl = (event as Event & { detail?: { url?: string } }).detail?.url;
      if (!nextUrl) return;

      event.preventDefault();
      if (!window.confirm(UNSAVED_CHANGES_MESSAGE)) return;

      navigationConfirmedRef.current = true;
      window.location.assign(nextUrl);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("turbo:before-visit", handleBeforeVisit);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("turbo:before-visit", handleBeforeVisit);
    };
  }, [hasUnsavedChanges, workspace?.note.readOnly, detailsDirty]);

  function applyWorkspaceResponse(payload: ConsultNoteWorkspaceResponse | null, fallbackAlert: string) {
    const nextWorkspace: ConsultNoteWorkspace | null = payload?.workspace || null;
    const nextDraftStatus: ConsultNoteDraftStatus | null = payload?.draftStatus || null;
    const nextFlash = payload?.flash || null;

    if (!nextWorkspace) {
      setDraftStatus(nextDraftStatus);
      setFlash(nextDraftStatus ? nextFlash : payload?.flash || { alert: fallbackAlert });
      setIsInitializing(nextDraftStatus?.state !== "failed");
      return;
    }

    startTransition(() => {
      setWorkspace(nextWorkspace);
      setDraftStatus(null);
      setFlash(nextFlash);
      setVisit({ ...nextWorkspace.visit });
      setNoteTitle(nextWorkspace.note.title);
      setDraftAddendumBody(nextWorkspace.draftAddendumBody || "");
      setSections(createFormSections(nextWorkspace.sections));
      setIsInitializing(false);
    });
  }

  async function performMutation(action: "save" | "regenerate" | "sign") {
    if (!workspace || !sections || !visit) return;

    setPendingAction(action);
    setFlash(null);

    const request = {
      url: action === "save" ? workspace.paths.save : action === "regenerate" ? workspace.paths.regenerate : workspace.paths.sign,
      method: action === "save" ? "PATCH" : "POST",
      fallbackAlert:
        action === "save"
          ? "Unable to save the consult note draft."
          : action === "regenerate"
            ? "Unable to regenerate the consult note."
            : "Unable to sign the consult note.",
      body: action === "regenerate" ? undefined : buildMutationFormData(noteTitle, sections, visit),
    };

    try {
      const response = await fetch(request.url, {
        method: request.method,
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": workspace.authenticityToken,
        },
        body: request.body,
        credentials: "same-origin",
      });
      const payload = await parseWorkspaceResponse(response);

      if (!response.ok) {
        applyWorkspaceResponse(payload, request.fallbackAlert);
        return;
      }

      applyWorkspaceResponse(payload, request.fallbackAlert);
    } catch (_error) {
      setFlash({ alert: request.fallbackAlert });
    } finally {
      setPendingAction(null);
    }
  }

  async function performAddendumMutation(action: "saveAddendum" | "signAddendum") {
    if (!workspace) return;

    setPendingAction(action);
    setFlash(null);

    const formData = new FormData();
    formData.append("consult_note[addendum_body]", draftAddendumBody);

    const request = {
      url: action === "saveAddendum" ? workspace.paths.addendum : workspace.paths.signAddendum,
      method: action === "saveAddendum" ? "PATCH" : "POST",
      fallbackAlert:
        action === "saveAddendum"
          ? "Unable to save the consult note addendum."
          : "Unable to sign the consult note addendum.",
    };

    try {
      const response = await fetch(request.url, {
        method: request.method,
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": workspace.authenticityToken,
        },
        body: formData,
        credentials: "same-origin",
      });
      const payload = await parseWorkspaceResponse(response);

      if (!response.ok) {
        applyWorkspaceResponse(payload, request.fallbackAlert);
        return;
      }

      applyWorkspaceResponse(payload, request.fallbackAlert);
    } catch (_error) {
      setFlash({ alert: request.fallbackAlert });
    } finally {
      setPendingAction(null);
    }
  }

  async function selectRevision(revisionId: number) {
    if (!workspace) return;

    setLoadingRevisionId(revisionId);

    try {
      const separator = workspace.paths.compare.includes("?") ? "&" : "?";
      const response = await fetch(`${workspace.paths.compare}${separator}revision_id=${revisionId}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        credentials: "same-origin",
      });
      const payload = await parseRevisionCompareResponse(response);

      if (!response.ok || !payload) {
        setFlash({ alert: "Unable to load the selected revision." });
        return;
      }

      setSelectedRevision(payload);
    } catch (_error) {
      setFlash({ alert: "Unable to load the selected revision." });
    } finally {
      setLoadingRevisionId(null);
    }
  }

  function updateTextSection(sectionKey: ConsultNoteTextSectionKey, value: string) {
    if (!sections) return;

    setSections((current) => ({
      ...(current as ConsultNoteFormSections),
      [sectionKey]: {
        ...(current as ConsultNoteFormSections)[sectionKey],
        value,
      },
    }));
  }

  function updateVisitDate(value: string) {
    if (!visit) return;

    setVisit((current) => ({
      ...(current as ConsultNoteVisit),
      date: value,
    }));
  }

  function updateVisitTime(value: string) {
    if (!visit) return;

    setVisit((current) => ({
      ...(current as ConsultNoteVisit),
      time: value,
    }));
  }

  function updatePhysicalExam(value: string) {
    if (!sections) return;

    setSections((current) => ({
      ...(current as ConsultNoteFormSections),
      physical_exam: {
        ...(current as ConsultNoteFormSections).physical_exam,
        value,
      },
    }));
  }

  function updateAssessmentCustomText(value: string) {
    if (!sections) return;

    setSections((current) => ({
      ...(current as ConsultNoteFormSections),
      assessment: {
        ...(current as ConsultNoteFormSections).assessment,
        customText: value,
      },
    }));
  }

  function toggleAssessmentOption(optionKey: string, checked: boolean) {
    if (!sections) return;

    setSections((current) => {
      const nextCurrent = current as ConsultNoteFormSections;
      let nextPlanValue = nextCurrent.plan.value;
      let optionWasSelected = false;
      let optionPlanTemplate = "";

      const nextOptions = nextCurrent.assessment.options.map((option) => {
        if (option.key !== optionKey) return option;

        optionWasSelected = option.selected;
        optionPlanTemplate = option.planTemplate;
        return { ...option, selected: checked };
      });

      if (checked && !optionWasSelected) {
        nextPlanValue = appendUniquePlanText(nextPlanValue, optionPlanTemplate);
      }

      return {
        ...nextCurrent,
        assessment: {
          ...nextCurrent.assessment,
          options: nextOptions,
          customText: optionKey === "custom" && !checked ? "" : nextCurrent.assessment.customText,
        },
        plan: {
          ...nextCurrent.plan,
          value: nextPlanValue,
        },
      };
    });
  }

  async function handleSave(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!workspace || workspace.note.readOnly || isMutating) return;
    if (workspace.note.finalized && draftAddendumBody.trim() && !window.confirm(DISCARD_ADDENDUM_DRAFT_CONFIRM_MESSAGE)) return;

    await performMutation("save");
  }

  async function handleRegenerate() {
    if (!workspace || workspace.note.readOnly || workspace.note.finalized || isMutating) return;
    if (!window.confirm(REGENERATE_CONFIRM_MESSAGE)) return;

    await performMutation("regenerate");
  }

  async function handleSign() {
    if (!workspace || workspace.note.readOnly || !workspace.note.canSign || workspace.note.finalized || isMutating) return;
    if (!window.confirm(SIGN_CONFIRM_MESSAGE)) return;

    await performMutation("sign");
  }

  async function handleExtractMissingLabs() {
    const extractAllUrl = workspace?.warnings?.missingLabExtraction?.extractAllUrl;
    if (!workspace || !extractAllUrl || isMutating) return;

    setPendingAction("extractMissingLabs");
    setFlash(null);

    try {
      const response = await fetch(extractAllUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": workspace.authenticityToken,
        },
        credentials: "same-origin",
      });
      const payload = await parseWorkspaceResponse(response);

      if (!response.ok) {
        applyWorkspaceResponse(payload, "Unable to queue missing lab extraction.");
        return;
      }

      applyWorkspaceResponse(payload, "Unable to queue missing lab extraction.");
    } catch (_error) {
      setFlash({ alert: "Unable to queue missing lab extraction." });
    } finally {
      setPendingAction(null);
    }
  }

  async function handleRetryTebraExport() {
    if (!workspace || !workspace.note.canRetryTebraExport || isMutating) return;
    if (!window.confirm(RETRY_TEBRA_EXPORT_CONFIRM_MESSAGE)) return;

    setPendingAction("retryTebraExport");
    setFlash(null);

    try {
      const response = await fetch(workspace.paths.retryTebraExport, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": workspace.authenticityToken,
        },
        credentials: "same-origin",
      });
      const payload = await parseWorkspaceResponse(response);

      if (!response.ok) {
        applyWorkspaceResponse(payload, "Unable to retry the Tebra submission.");
        return;
      }

      applyWorkspaceResponse(payload, "Unable to retry the Tebra submission.");
    } catch (_error) {
      setFlash({ alert: "Unable to retry the Tebra submission." });
    } finally {
      setPendingAction(null);
    }
  }

  async function handleSaveAddendum() {
    if (!workspace || workspace.note.readOnly || !workspace.note.finalized || isMutating) return;

    await performAddendumMutation("saveAddendum");
  }

  async function handleSignAddendum() {
    if (!workspace || workspace.note.readOnly || !workspace.note.finalized || !workspace.note.canSign || isMutating) return;
    if (!window.confirm(SIGN_ADDENDUM_CONFIRM_MESSAGE)) return;

    await performAddendumMutation("signAddendum");
  }

  function openHistoryView() {
    setFlash(null);
    setSelectedRevision(null);
    setLoadingRevisionId(null);
    setActiveView("history");
  }

  function closeHistoryView() {
    setActiveView("editor");
  }

  async function handleDuplicate() {
    if (!workspace?.paths.duplicate || workspace.note.readOnly || isMutating) return;
    if (hasUnsavedChanges && !window.confirm("Duplicate the last saved note? Your unsaved changes will not be included and will be discarded when the new draft opens.")) return;

    setPendingAction("duplicate");
    setFlash(null);
    try {
      const response = await fetch(workspace.paths.duplicate, {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": workspace.authenticityToken },
        credentials: "same-origin",
      });
      const payload = await response.json();
      if (!response.ok || !payload.redirectPath) {
        setFlash({ alert: payload.alert || "Unable to duplicate the consult note." });
        return;
      }
      navigationConfirmedRef.current = true;
      window.location.assign(payload.redirectPath);
    } catch (_error) {
      navigationConfirmedRef.current = false;
      setFlash({ alert: "Unable to duplicate the consult note." });
    } finally {
      setPendingAction(null);
    }
  }

  async function handleCopyNote() {
    if (!workspace || !sections || !visit) return;

    if (copyResetTimerRef.current !== null) {
      window.clearTimeout(copyResetTimerRef.current);
      copyResetTimerRef.current = null;
    }

    const noteText = buildNoteCopyText(noteTitle, visit, workspace.sectionOrder, sections, workspace.signedAddendums);

    try {
      await copyTextToClipboard(noteText);
      setCopyState("copied");
    } catch (_error) {
      setCopyState("failed");
    }

    copyResetTimerRef.current = window.setTimeout(() => {
      setCopyState("idle");
      copyResetTimerRef.current = null;
    }, 1500);
  }

  function renderSection(sectionKey: ConsultNoteSectionKey) {
    if (!workspace || !sections) return null;

    if (sectionKey === "assessment") {
      return (
        <AssessmentSectionCard
          key={sectionKey}
          section={sections.assessment}
          readOnly={workspace.note.readOnly}
          disabled={isMutating}
          onToggle={toggleAssessmentOption}
          onCustomTextChange={updateAssessmentCustomText}
        />
      );
    }

    if (sectionKey === "physical_exam") {
      return (
        <PhysicalExamSectionCard
          key={sectionKey}
          section={sections.physical_exam}
          readOnly={workspace.note.readOnly}
          disabled={isMutating}
          onChange={updatePhysicalExam}
          onAddExam={() => updatePhysicalExam(sections.physical_exam.defaultText)}
        />
      );
    }

    const section = sections[sectionKey] as ConsultNoteFormSections[ConsultNoteTextSectionKey];
    const headerAction = sectionKey === "hpi" ? (
      <a
        href={workspace.paths.faceSheet}
        className="app-button-secondary"
        target="_blank"
        rel="noopener noreferrer"
      >
        Open facesheet
      </a>
    ) : undefined;

    return (
      <TextSectionCard
        key={sectionKey}
        section={section}
        readOnly={workspace.note.readOnly}
        disabled={isMutating}
        headerAction={headerAction}
        onChange={(value) => updateTextSection(sectionKey as ConsultNoteTextSectionKey, value)}
      />
    );
  }

  if (isRegenerating || !workspace || !sections || !visit) {
    const isBuildPending = !isRegenerating && (draftStatus?.state === "queued" || draftStatus?.state === "building" || isInitializing);
    const loadHeading = isRegenerating
      ? "Regenerating Consult Note"
      : isBuildPending
        ? "Building Draft Consult Note"
        : draftStatus?.state === "failed"
          ? "Unable to build consult note"
          : "Unable to load consult note";
    const loadMessage = isRegenerating
      ? "Regenerating this consult note from the latest template. We are calling out to the AI and waiting for a response. This page will update automatically when it is ready."
      : isBuildPending
        ? draftStatus?.message || `Building the first consult note draft for ${bootstrap.patient.fullName}. We are calling out to the AI and waiting for a response. This page will update automatically when it is ready.`
        : draftStatus?.message || "The consult note draft could not be loaded. Retry to request the draft again.";
    const spinnerLabel = isRegenerating ? "Regenerating Consult Note" : "Building Draft Consult Note";

    return (
      <div className="space-y-4">
        <FlashBanner flash={flash} />

        <section className="app-surface p-6">
          <h2 className="text-2xl font-semibold text-neutral-950 dark:text-white">{loadHeading}</h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-neutral-600 dark:text-neutral-300">{loadMessage}</p>

          {isRegenerating || isBuildPending ? (
            <div className="mt-5 flex items-center gap-3 text-sm font-medium text-neutral-700 dark:text-neutral-200">
              <span
                aria-hidden="true"
                className="inline-flex h-5 w-5 animate-spin rounded-full border-2 border-emerald-200 border-t-emerald-700 dark:border-emerald-950 dark:border-t-emerald-300"
              />
              <span>{spinnerLabel}</span>
            </div>
          ) : null}

          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              className="app-button-primary"
              disabled={isRegenerating || isBuildPending}
              onClick={() => {
                setFlash(null);
                setDraftStatus(null);
                setRetryBuild(true);
                setLoadAttempt((current) => current + 1);
              }}
            >
              {isRegenerating ? "Regenerating..." : isBuildPending ? "Building..." : "Retry"}
            </button>
            <a href={bootstrap.patient.patientPath} className="app-button-secondary">
              View Patient
            </a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <FlashBanner flash={flash} />

      {workspace.paths.details && <nav aria-label="Consult sections" className="flex gap-4 border-b border-neutral-200 dark:border-neutral-700">
        <button type="button" className={`px-3 py-3 text-sm font-semibold ${activeView !== "billing" ? "border-b-2 border-emerald-700 text-emerald-700 dark:text-emerald-300" : "text-neutral-500"}`} aria-pressed={activeView !== "billing"} onClick={() => setActiveView("editor")}>Clinical note</button>
        <button type="button" className={`px-3 py-3 text-sm font-semibold ${activeView === "billing" ? "border-b-2 border-emerald-700 text-emerald-700 dark:text-emerald-300" : "text-neutral-500"}`} aria-pressed={activeView === "billing"} onClick={() => setActiveView("billing")}>Billing & plan{detailsDirty ? " • Unsaved" : ""}</button>
      </nav>}
      {workspace.paths.details && <div hidden={activeView !== "billing"}>
        <InternalDetailsPanel key={workspace.note.id} url={workspace.paths.details} authenticityToken={workspace.authenticityToken} patientName={workspace.patient.fullName} visitDate={workspace.visit.date} onDirtyChange={setDetailsDirty} />
      </div>}
      <div hidden={activeView === "billing"} className="space-y-4">
      <WorkspaceHeader
        workspace={workspace}
        noteTitle={noteTitle}
        isMutating={isMutating}
        pendingAction={pendingAction}
        copyState={copyState}
        formId={EDITOR_FORM_ID}
        onTitleChange={setNoteTitle}
        onCopyNote={() => void handleCopyNote()}
        onDuplicate={() => void handleDuplicate()}
        onOpenHistory={openHistoryView}
        showHistoryButton={activeView !== "history"}
        onSign={handleSign}
        onRegenerate={handleRegenerate}
      />
      {activeView === "history" ? (
        <RevisionHistoryPanel
          revisions={revisionHistory}
          selectedRevision={selectedRevision}
          loadingRevisionId={loadingRevisionId}
          onBack={closeHistoryView}
          onSelectRevision={selectRevision}
        />
      ) : (
        <>
          <MultipleConsultIntakesNotice warning={workspace.warnings?.multipleConsultIntakes} />
          {workspace.warnings?.documentHistory?.length ? (
            <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
              <p className="font-semibold">Document history needs review</p>
              <ul className="mt-2 list-disc pl-5">
                {workspace.warnings.documentHistory.map((warning) => <li key={warning}>{warning}</li>)}
              </ul>
            </div>
          ) : null}
          <TebraExportFailureNotice
            note={workspace.note}
            disabled={isMutating}
            pending={pendingAction === "retryTebraExport"}
            onRetry={() => void handleRetryTebraExport()}
          />
          <MissingLabExtractionNotice
            warning={workspace.warnings?.missingLabExtraction}
            disabled={isMutating}
            pending={pendingAction === "extractMissingLabs"}
            onExtractAll={handleExtractMissingLabs}
          />
          <AiTracePanel aiTrace={workspace.aiTrace} />

          <form id={EDITOR_FORM_ID} className="space-y-4" onSubmit={handleSave}>
            <VisitDetailsCard
              visit={visit}
              readOnly={workspace.note.readOnly}
              disabled={isMutating}
              onDateChange={updateVisitDate}
              onTimeChange={updateVisitTime}
            />
            {workspace.sectionOrder.map((sectionKey) => renderSection(sectionKey))}
            {workspace.note.finalized ? (
              <AddendumSectionCard
                signedAddendums={workspace.signedAddendums}
                draftBody={draftAddendumBody}
                canSign={workspace.note.canSign}
                readOnly={workspace.note.readOnly}
                disabled={isMutating}
                pendingAction={pendingAction}
                onDraftBodyChange={setDraftAddendumBody}
                onSaveDraft={() => void handleSaveAddendum()}
                onSign={() => void handleSignAddendum()}
              />
            ) : null}

            <FormActions
              note={workspace.note}
              isMutating={isMutating}
              pendingAction={pendingAction}
              formId={EDITOR_FORM_ID}
              onSign={handleSign}
            />
          </form>
        </>
      )}
      </div>
    </div>
  );
}
